import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { HdButton, HdIcon, HdIconButton } from '../../../components';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import { useScheduleCall } from '../../../support/hooks/useScheduleCall';
import useService from '../../../injector/hooks/useService';
import { EntityUIState } from '../../../core/interfaces/entitiy-ui-state';
import { NPSService } from '../../../core/services/nps.service';
import {
  NPS_POST_SUBMISSION_REVIEW_URL,
  NPS_POST_SUBMISSION_REVIEW_LOGO,
  NPS_SCHEDULE_CALL_LINK,
  RATING_STAR_IMAGES, NPS_POST_SUBMISSION_REVIEW_LABEL
} from './constants';
import { NPS_DIALOG_DATA_IDS } from './data-ids';
import styles from './NPSDialog.module.scss';
import { NPSDialogTrackingEvents } from './tracking';

export interface NPSDialogPostSubmissionContentProps {
  rating: number;
  scheduleCallLink?: string;
  scheduleCallLinkFetchState: EntityUIState;
  onClose: () => void;
}

const dataIds = NPS_DIALOG_DATA_IDS;

export function NPSDialogPostSubmissionContent({
  rating,
  scheduleCallLink,
  scheduleCallLinkFetchState,
  onClose
}: NPSDialogPostSubmissionContentProps) {
  const [scheduleCallClicked, setScheduleCallClicked] = useState(false);

  const { eventTrack } = useAnalyticsTracker();
  const npsService = useService(NPSService);
  const { openScheduleCallLink } = useScheduleCall();

  const remindMeLaterClick = () => {
    npsService.remind3rdPartyRatingLater(rating).subscribe();

    eventTrack({
      action: NPSDialogTrackingEvents.remindLaterClick
    });

    setTimeout(() => {
      onClose();
    }, 1000);
  };

  const isProcessingScheduleCallClick =
    scheduleCallClicked && scheduleCallLinkFetchState === EntityUIState.LOADING;

  useEffect(() => {
    if (scheduleCallClicked && scheduleCallLinkFetchState === EntityUIState.IDLE) {
      openScheduleCallLink(
        scheduleCallLink || NPS_SCHEDULE_CALL_LINK,
        NPSDialogTrackingEvents.scheduleCallClick,
        'nps-dialog'
      );

      onClose();
    }
  }, [scheduleCallClicked, scheduleCallLinkFetchState]);

  return (
    <div className='dialog-body'>
      <div className={styles.closeIcon}>
        <HdIconButton
          verticalEdge='both'
          horizontalEdge='both'
          onClick={() => {
            eventTrack({
              action: NPSDialogTrackingEvents.closeDialogClickAfterSubmission
            });

            onClose();
          }}
          dataId={dataIds.close}
        >
          <HdIcon name='close' size={3} />
        </HdIconButton>
      </div>

      <div className='d-flex justify-center'>
        <img
          data-id={dataIds.starIcon}
          alt=''
          style={{ height: '48px' }}
          src={RATING_STAR_IMAGES[rating]}
        />
      </div>

      {rating >= 9 ? (
        <div className='center-flex-col'>
          <div className='text-subheading-2 text-center mt-6' data-id={dataIds.postSubmissionTitle}>
            <span data-id={dataIds.postSubmissionTitlePositive}>
              Thank you for your feedback! <span style={{ fontSize: '16px' }}>🤩</span>
            </span>
          </div>
        </div>
      ) : null}

      <div
        className={clsx(
          'text-center mx-auto',
          rating >= 9 ? 'text-secondary mt-3' : 'text-default mt-4'
        )}
        data-id={dataIds.postSubmissionSubText}
        style={{ maxWidth: '90%' }}
      >
        {rating <= 8 ? (
          <span data-id={dataIds.postSubmissionSubTextNegative}>
            We appreciate your feedback and are sorry we didn’t meet your expectations. Let’s
            connect to understand your concerns and improve your experience.
          </span>
        ) : (
          <span data-id={dataIds.postSubmissionSubTextPositive}>
            Can you help us spread the word by leaving some happy comments about Hevo on {NPS_POST_SUBMISSION_REVIEW_LABEL}?
          </span>
        )}
      </div>

      {rating >= 9 ? (
        <>
          <div className='center-flex-row justify-center mt-5'>
            <span className={clsx('d-flex p-2', styles.promoContent)}>
              <HdIcon name='coupon' className='mr-2' />
              <span className='text-body-2'>Submit your review and earn a $25 gift voucher.</span>
            </span>
          </div>

          <div className='center-flex-col mt-6 mb-4'>
            <HdButton
              dataId={dataIds.reviewButton}
              tag='a'
              target='_blank'
              variation='outline'
              href={NPS_POST_SUBMISSION_REVIEW_URL}
              iconSrc={NPS_POST_SUBMISSION_REVIEW_LOGO}
              onClick={() => {
                eventTrack({
                  action: NPSDialogTrackingEvents.promoReviewButtonClick
                });
              }}
            >
              {NPS_POST_SUBMISSION_REVIEW_LABEL}
            </HdButton>
          </div>

          <div className='d-flex justify-center'>
            <HdButton
              dataId={dataIds.remindLater}
              variation='flat'
              onClick={remindMeLaterClick}
            >
              Remind me later
            </HdButton>
          </div>
        </>
      ) : (
        <div className='d-flex justify-center mt-5'>
          <HdButton
            dataId={dataIds.scheduleCall}
            icon='schedule-call'
            disabled={isProcessingScheduleCallClick}
            inProgress={isProcessingScheduleCallClick}
            onClick={() => {
              setScheduleCallClicked(true);
            }}
          >
            Schedule a Call with Us
          </HdButton>
        </div>
      )}
    </div>
  );
}
